import { StateCreator } from 'zustand'
import { SharedSlice } from '../../../../slice'
import { exceptionMessageHandler } from '$utils'
import { payAdjustmentPackageService } from '$services'
import { PackageDetail } from '../../_common/interfaces'

interface PayAdjustmentPackagesState {
  payAdjustmentPackages: PackageDetail[]
  selectedPayAdjustmentPackage?: PackageDetail
}

export interface PayAdjustmentPackagesSlice extends PayAdjustmentPackagesState {
  /**
   * Get enabled Pay Adjustment Packages list
   */
  getPayAdjustmentPackages: () => Promise<void>

  /**
   * Get a Pay Adjustment Package by id
   * @param packageId
   */
  getPayAdjustmentPackage: (packageId: string) => Promise<void>

  /**
   * Resets Pay Adjustment Packages slice state
   */
  resetPayAdjustmentPackagesSlice: () => void
}

const initialState: PayAdjustmentPackagesState = {
  payAdjustmentPackages: [],
  selectedPayAdjustmentPackage: undefined,
}

const createPayAdjustmentPackagesSlice: StateCreator<
  PayAdjustmentPackagesSlice & SharedSlice,
  [],
  [],
  PayAdjustmentPackagesSlice
> = (set) => ({
  // State variables
  ...initialState,

  // State setters
  resetPayAdjustmentPackagesSlice: () => set(initialState),

  // State API calls
  getPayAdjustmentPackages: async (): Promise<void> => {
    set(() => ({ isLoading: true }))

    return payAdjustmentPackageService
      .getAll()
      .then((result) => {
        set(() => ({
          payAdjustmentPackages: result.filter(
            (payAdjustmentPackage) => payAdjustmentPackage.activation != null
          ),
        }))
      })
      .catch((error) => {
        set(() => ({ payAdjustmentPackages: [] }))
        exceptionMessageHandler(
          error,
          'Failed to retrieve pay adjustment packages'
        )
      })
      .finally(() => {
        set(() => ({ isLoading: false }))
      })
  },

  getPayAdjustmentPackage: async (packageId: string): Promise<void> => {
    // clear the previous selection so a failed or in-flight fetch can never
    // display another package's details
    set(() => ({ isLoading: true, selectedPayAdjustmentPackage: undefined }))

    return payAdjustmentPackageService
      .getById(packageId)
      .then((result) => set(() => ({ selectedPayAdjustmentPackage: result })))
      .catch((error) => {
        set(() => ({ selectedPayAdjustmentPackage: undefined }))
        exceptionMessageHandler(
          error,
          'Failed to retrieve the pay adjustment package'
        )
      })
      .finally(() => {
        set(() => ({ isLoading: false }))
      })
  },
})

export default createPayAdjustmentPackagesSlice
